import { resolveOwnerSpaceId } from '@/lib/currentOrg'
import type { SpaceItem } from '@/types'

/** `GET /org/main/list` 列表项子集（对齐 org-console OrgItem）。 */
export interface OrgListItem {
  id: string
  name?: string
  name_i18n?: string
  logo?: string
  /** 1=企业 / 2=个人；也兼容字符串 `workspace` / `personal`。 */
  space_type?: number | string
  my_role?: string
  is_owner?: boolean
  /** 1=active / 2=suspended / 3=archived / 4=dissolving */
  status?: number
  is_dissolving?: boolean
}

const ACTIVE = 1

function pickName(item: OrgListItem, lang: string): string {
  const fallback = item.name?.trim() || item.id
  const raw = item.name_i18n?.trim()
  if (!raw) return fallback
  try {
    const map = JSON.parse(raw) as Record<string, string>
    const exact = map[lang]?.trim()
    if (exact) return exact
    const prefix = lang.split('-')[0]
    if (prefix !== lang) {
      const byPrefix = map[prefix]?.trim()
      if (byPrefix) return byPrefix
    }
  } catch {
    /* name_i18n 坏了就用字面名 */
  }
  return fallback
}

function isPersonal(space_type: OrgListItem['space_type']): boolean {
  if (space_type == null) return false
  const normalized = String(space_type).trim().toLowerCase()
  return normalized === '2' || normalized === 'personal'
}

function roleOf(item: OrgListItem): SpaceItem['role'] | undefined {
  if (
    item.my_role === 'owner' ||
    item.my_role === 'admin' ||
    item.my_role === 'member'
  ) {
    return item.my_role
  }
  if (item.is_owner) return 'owner'
  return undefined
}

/** 可进入的空间：只要 active，排除解散冷静期。个人在前由调用方分组。 */
export function usableOrgsToSpaces(
  list: OrgListItem[] | null | undefined,
  lang: string,
): SpaceItem[] {
  const spaces: SpaceItem[] = []
  for (const item of list ?? []) {
    const id = item.id?.trim()
    if (!id) continue
    if (item.status !== ACTIVE || item.is_dissolving) continue
    const personal = isPersonal(item.space_type)
    spaces.push({
      id,
      name: pickName(item, lang),
      logo: item.logo?.trim() || undefined,
      type: personal ? 'personal' : 'workspace',
      role: personal ? undefined : roleOf(item),
    })
  }
  return spaces
}

/**
 * 列表回来后选当前空间：
 * 已选且仍在列表里 → 保留；
 * 否则个人空间，再否则第一项。
 */
export function pickCurrentSpaceId(
  spaces: SpaceItem[],
  current: string | null,
): string | null {
  if (current && spaces.some((s) => s.id === current)) return current
  const resolved = resolveOwnerSpaceId(current)
  if (resolved && spaces.some((s) => s.id === resolved)) return resolved
  return spaces.find((s) => s.type === 'personal')?.id ?? spaces[0]?.id ?? null
}
